import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function usePortfolioData() {
  const { isAuthenticated, authHeaders } = useAuth();
  const [portfolio, setPortfolio] = useState({ assets: [], summary: {} });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [perfStats, setPerfStats] = useState(null);
  const [journal, setJournal] = useState(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [shareCard, setShareCard] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);

  const fetchPortfolio = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) { setLoading(false); return; }
      const response = await axios.get(`${API}/portfolio`, { headers });
      setPortfolio(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      toast.error("Failed to fetch portfolio");
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/portfolio/analytics`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  }, [authHeaders]);

  const fetchTrades = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/performance/trades`, { headers });
      setTrades(response.data.trades || []);
    } catch (error) { console.error("Failed to fetch trades:", error); }
  }, [authHeaders]);

  const fetchPerfStats = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/performance/stats`, { headers });
      setPerfStats(response.data);
    } catch (error) { console.error("Failed to fetch perf stats:", error); }
  }, [authHeaders]);

  useEffect(() => {
    fetchPortfolio();
    fetchAnalytics();
    if (isAuthenticated) {
      fetchTrades();
      fetchPerfStats();
    }
  }, [isAuthenticated, fetchPortfolio, fetchAnalytics, fetchTrades, fetchPerfStats]);

  const removeAsset = useCallback(async (assetId) => {
    try {
      await axios.delete(`${API}/portfolio/${assetId}`, { headers: authHeaders() });
      toast.success("Asset removed from portfolio");
      fetchPortfolio();
      fetchAnalytics();
    } catch (error) {
      console.error("Error removing asset:", error);
      toast.error("Failed to remove asset");
    }
  }, [authHeaders, fetchPortfolio, fetchAnalytics]);

  const closeTrade = useCallback(async (tradeId, closePrice) => {
    try {
      await axios.post(`${API}/performance/close-trade`,
        { trade_id: tradeId, close_price: closePrice },
        { headers: authHeaders() }
      );
      toast.success("Trade closed");
      fetchTrades();
      fetchPerfStats();
    } catch (error) {
      toast.error("Failed to close trade");
    }
  }, [authHeaders, fetchTrades, fetchPerfStats]);

  const deleteTrade = useCallback(async (tradeId) => {
    try {
      await axios.delete(`${API}/performance/${tradeId}`, { headers: authHeaders() });
      toast.success("Trade removed");
      fetchTrades();
      fetchPerfStats();
    } catch (error) {
      toast.error("Failed to remove trade");
    }
  }, [authHeaders, fetchTrades, fetchPerfStats]);

  const fetchJournal = useCallback(async () => {
    setJournalLoading(true);
    try {
      const headers = authHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/journal/analysis`, { headers });
      setJournal(response.data);
    } catch (error) {
      console.error("Journal error:", error);
    } finally {
      setJournalLoading(false);
    }
  }, [authHeaders]);

  const generateShareCard = useCallback(async () => {
    setShareLoading(true);
    try {
      const response = await axios.post(`${API}/share/create`, {}, { headers: authHeaders() });
      setShareCard(response.data);
      const shareUrl = `${API}/share/page/${response.data.share_id}`;
      navigator.clipboard.writeText(shareUrl);
      toast.success("Share card created & public link copied!");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Need at least 1 closed trade to share");
      } else {
        toast.error("Failed to generate share card");
      }
    } finally {
      setShareLoading(false);
    }
  }, [authHeaders]);

  return {
    portfolio, analytics, loading, trades, perfStats,
    journal, journalLoading, shareCard, shareLoading,
    removeAsset, closeTrade, deleteTrade, fetchJournal, generateShareCard,
  };
}
